import { type BulkOperationSummary } from "../../api/types.ts";
import { isTerminalBulkStatus } from "../../api/utils/bulkStatusUtils.ts";
import { useCommittedRef } from "@sg-protect/components";
import { useEventBusListener } from "@sg-protect/federation-runtime";
import { useCallback, useEffect, useState } from "react";
import { BULK_OPERATION_STARTED_EVENT } from "../../events.ts";
import { type RemediationSelection, doesRunningSelectionCoverRow } from "./RemediationSelection.ts";

interface TrackedSelection {
    selection: RemediationSelection;
    startedAt: number;
    isObserved: boolean;
}

const MAX_UNOBSERVED_SELECTION_AGE_MS = 15_000;

const UNOBSERVED_SELECTION_CHECK_INTERVAL_MS = 5_000;

function parseIdsKey(idsKey: string | null) {
    return new Set(idsKey ? idsKey.split(",") : []);
}

function toSummariesKey(summaries: BulkOperationSummary[]) {
    return summaries.map(summary => `${summary.bulkId}:${summary.status}`).join(",");
}

function trackSelection(prev: ReadonlyMap<string, TrackedSelection>, bulkId: string, selection: RemediationSelection) {
    const next = new Map(prev);
    next.set(bulkId, { selection, startedAt: Date.now(), isObserved: false });

    return next;
}

interface ReconcileContext {
    inProgressIds: ReadonlySet<string>;
    summaryByBulkId: ReadonlyMap<string, BulkOperationSummary>;
    now: number;
}

function reconcileEntry(bulkId: string, entry: TrackedSelection, context: ReconcileContext): TrackedSelection | null {
    const { inProgressIds, summaryByBulkId, now } = context;
    const summary = summaryByBulkId.get(bulkId);
    const isObserved = entry.isObserved || inProgressIds.has(bulkId);

    const isDone =
        (summary && isTerminalBulkStatus(summary.status)) ||
        (!isObserved && now - entry.startedAt > MAX_UNOBSERVED_SELECTION_AGE_MS);

    if (isDone) {
        return null;
    }

    return isObserved === entry.isObserved ? entry : { ...entry, isObserved };
}

function computeReconciledSelections(
    prev: ReadonlyMap<string, TrackedSelection>,
    inProgressIds: ReadonlySet<string>,
    summaries: BulkOperationSummary[]
): ReadonlyMap<string, TrackedSelection> {
    const context: ReconcileContext = {
        inProgressIds,
        summaryByBulkId: new Map(summaries.map(summary => [summary.bulkId, summary])),
        now: Date.now()
    };

    let next: Map<string, TrackedSelection> | undefined;

    for (const [bulkId, entry] of prev) {
        const reconciled = reconcileEntry(bulkId, entry, context);

        if (reconciled === null) {
            next ??= new Map(prev);
            next.delete(bulkId);
        } else if (reconciled !== entry) {
            next ??= new Map(prev);
            next.set(bulkId, reconciled);
        }
    }

    return next ?? prev;
}

export function useRunningSelections(inProgressIdsKey: string, summaries: BulkOperationSummary[], isSkipped = false) {
    const [trackedSelections, setTrackedSelections] = useState<ReadonlyMap<string, TrackedSelection>>(new Map());

    // A skipped provider stops polling summaries, so a selection kept here would never see its terminal one.
    useEffect(() => {
        if (isSkipped) {
            setTrackedSelections(prev => (prev.size === 0 ? prev : new Map()));
        }
    }, [isSkipped]);

    useEventBusListener(BULK_OPERATION_STARTED_EVENT, ({ bulkId, selection }) => {
        if (!selection) {
            return;
        }

        setTrackedSelections(prev => trackSelection(prev, bulkId, selection));
    });

    const reconcileSelections = useCallback(
        (inProgressIds: ReadonlySet<string>, latestSummaries: BulkOperationSummary[]) => {
            setTrackedSelections(prev => computeReconciledSelections(prev, inProgressIds, latestSummaries));
        },
        []
    );

    const inProgressIdsRef = useCommittedRef(inProgressIdsKey);
    const summariesRef = useCommittedRef(summaries);
    const summariesKey = toSummariesKey(summaries);

    useEffect(() => {
        reconcileSelections(parseIdsKey(inProgressIdsKey), summariesRef.current ?? []);
    }, [inProgressIdsKey, summariesKey, reconcileSelections, summariesRef]);

    useEffect(() => {
        if (trackedSelections.size === 0) {
            return;
        }

        const intervalId = setInterval(() => {
            reconcileSelections(parseIdsKey(inProgressIdsRef.current), summariesRef.current ?? []);
        }, UNOBSERVED_SELECTION_CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [trackedSelections.size, reconcileSelections, inProgressIdsRef, summariesRef]);

    const isSelectionRunning = useCallback(
        (selection: RemediationSelection) => {
            return Array.from(trackedSelections.values()).some(entry =>
                doesRunningSelectionCoverRow(entry.selection, selection)
            );
        },
        [trackedSelections]
    );

    return { isSelectionRunning };
}
